// ReportsPage.js - Complete optimized version
import React, {
  useState,
  useEffect,
  useCallback,
  useRef,
  } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
   Search,
  FileText,

  ChevronRight,
  Menu,
  X,
  ExternalLink,
  Trash2,
  RefreshCw,
  AlertCircle,
  Info,
} from "lucide-react";
import { apiService } from "../services/api";
import LoadingScreen from "../components/LoadingScreen";
import Sidebar from "../components/Sidebar";
import TableComponent from "../components/TableComponent";
import Pagination from "../components/Pagination";
import { useToast } from "../contexts/ToastContext";
import useTableConfiguration from "../hooks/useTableConfiguration";

const ReportsPage = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { showToast } = useToast();
  const searchTimeoutRef = useRef(null);
  const initializingRef = useRef(false);

  // Advanced optimization refs for smart caching
  const previousFiltersRef = useRef(null);
  const previousGeneralSearchRef = useRef(null);
  const tabCountsCacheRef = useRef({});
  const countTimeoutRef = useRef(null);
  const countCallInProgressRef = useRef(false);

  const {
    configurations,
    loading: configLoading,
    getConfigurationForReportType,
    getTableHeaders,
  } = useTableConfiguration();

  // State management
  const [sidebarOpen, setSidebarOpen] = useState(true);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [generalSearch, setGeneralSearch] = useState("");
  const [activeTab, setActiveTab] = useState("RTC");
  const [tabCounts, setTabCounts] = useState({});

  // Info popup state
  const [showSearchInfo, setShowSearchInfo] = useState(false);
  const searchInfoRef = useRef(null);

  // Bookmark & PDF viewer states
  const [bookmarkedIds, setBookmarkedIds] = useState([]);
  const [pdfReportId, setPdfReportId] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(null);

  // Filter states
  const [filters, setFilters] = useState({
    report_type: "",
    sales_area: "",
    entity: "",
    customer_name: "",
    customer_entity: [],
    country_name: "",
    site_name: "",
    date_from: "",
    date_to: "",
    failure_reason: "",
    part_number: "",
    description: "",
    rig_model: "",
    rig_id: "",
  });

  // Tab configuration
  const tabs = [
    { key: "RTC", label: "Rock tool cards", reportType: "RTC" },
    { key: "CSV", label: "Customer site visits", reportType: "CSV" },
    { key: "SA", label: "Site audits", reportType: "SA" },
    { key: "RA", label: "Rig audits", reportType: "RA" },
  ];

  // Memoized function to create filter signature for comparison
  const createFilterSignature = useCallback((filters, generalSearch) => {
    // Create a signature that includes both filters and general search
    const filtersCopy = { ...filters };

    // Handle date filters consistently - only include if both present
    if (!filtersCopy.date_from || !filtersCopy.date_to) {
      delete filtersCopy.date_from;
      delete filtersCopy.date_to;
    }

    // Remove report_type from signature since it's handled per tab
    delete filtersCopy.report_type;

    return JSON.stringify({
      filters: filtersCopy,
      generalSearch: generalSearch?.trim() || "",
    });
  }, []);

  const fetchTabCounts = useCallback(
    async (currentFilters, generalSearchTerm = "", forceRefresh = false) => {
      try {
        // Create signature for current state
        const currentSignature = createFilterSignature(
          currentFilters,
          generalSearchTerm
        );

        // Check if we need to update counts
        const shouldUpdate =
          forceRefresh ||
          !previousFiltersRef.current ||
          !previousGeneralSearchRef.current ||
          previousFiltersRef.current !== currentSignature;

        if (!shouldUpdate) {
          return;
        }

        // IMPORTANT: Check if a count call is already in progress
        if (countCallInProgressRef.current && !forceRefresh) {
          return;
        }

        // Clear existing timeout
        if (countTimeoutRef.current) {
          clearTimeout(countTimeoutRef.current);
        }

        // Set flag to prevent duplicate calls
        countCallInProgressRef.current = true;

        // Debounce the count API calls with longer delay
        countTimeoutRef.current = setTimeout(async () => {
          const baseFilters = { ...currentFilters };
          delete baseFilters.report_type;

          // Handle date filters for tab counts
          if (!baseFilters.date_from || !baseFilters.date_to) {
            delete baseFilters.date_from;
            delete baseFilters.date_to;
          }

          const trimmedTerm = generalSearchTerm ? generalSearchTerm.trim() : "";
          const isWildcardSearch = trimmedTerm.startsWith("*");
          const hasGeneralSearch = trimmedTerm !== "" && !isWildcardSearch;

          try {
            if (isWildcardSearch) {
              // Wildcard → count using part_number filter only
              const partNumberTerm = trimmedTerm.slice(1).trim();
              const countPromises = tabs.map(async (tab) => {
                try {
                  const tabFilters = {
                    ...baseFilters,
                    report_type: tab.reportType,
                    ...(partNumberTerm ? { part_number: partNumberTerm } : {}),
                  };
                  const result = await apiService.getFilteredCount(tabFilters);
                  return { key: tab.key, count: result.count };
                } catch (error) {
                  console.error(`Error fetching count for tab ${tab.key}:`, error);
                  return { key: tab.key, count: 0 };
                }
              });

              const results = await Promise.all(countPromises);
              const counts = results.reduce((acc, { key, count }) => {
                acc[key] = count;
                return acc;
              }, {});

              setTabCounts(counts);
              tabCountsCacheRef.current = counts;
            } else if (hasGeneralSearch) {
              // For combined search, fetch all tab counts in parallel using count API
              const countPromises = tabs.map(async (tab) => {
                try {
                  const tabFilters = {
                    ...baseFilters,
                    report_type: tab.reportType,
                  };
                  const result = await apiService.getCombinedSearchCount(
                    tabFilters,
                    trimmedTerm
                  );
                  return { key: tab.key, count: result.count };
                } catch (error) {
                  console.error(
                    `Error fetching count for tab ${tab.key}:`,
                    error
                  );
                  return { key: tab.key, count: 0 };
                }
              });

              const results = await Promise.all(countPromises);
              const counts = results.reduce((acc, { key, count }) => {
                acc[key] = count;
                return acc;
              }, {});

              setTabCounts(counts);
              tabCountsCacheRef.current = counts;
            } else {
              // For filter-only search, batch the requests more efficiently

              // Create a single batched request if your API supports it
              // Otherwise, use controlled parallel requests
              const countPromises = tabs.map(async (tab) => {
                try {
                  const tabFilters = {
                    ...baseFilters,
                    report_type: tab.reportType,
                  };
                  const result = await apiService.getFilteredCount(tabFilters);
                  return { key: tab.key, count: result.count };
                } catch (error) {
                  console.error(
                    `Error fetching count for tab ${tab.key}:`,
                    error
                  );
                  return { key: tab.key, count: 0 };
                }
              });

              const results = await Promise.all(countPromises);
              const counts = results.reduce((acc, { key, count }) => {
                acc[key] = count;
                return acc;
              }, {});

              setTabCounts(counts);
              tabCountsCacheRef.current = counts;
            }

            // Update tracking refs after successful fetch
            previousFiltersRef.current = currentSignature;
            previousGeneralSearchRef.current = currentSignature;
          } catch (error) {
            console.error("Error fetching tab counts:", error);
            setTabCounts({});
          } finally {
            // Reset the in-progress flag
            countCallInProgressRef.current = false;
          }
        }, 500); // Increased debounce delay from 300ms to 500ms
      } catch (error) {
        console.error("Error in fetchTabCounts:", error);
        setTabCounts({});
        countCallInProgressRef.current = false;
      }
    },
    [tabs, createFilterSignature]
  );

  // Wait for configuration to load before initializing
  useEffect(() => {
    if (configLoading) {
      return;
    }

    if (configurations.length === 0) {
      return;
    }

    // Only initialize once configurations are loaded
    if (!initializingRef.current) {
      initializePage();
    }
  }, [configLoading, configurations.length]);

  const initializePage = () => {
    initializingRef.current = true;

    // Parse URL parameters
    const urlPage = parseInt(searchParams.get("page")) || 1;
    const urlPageSize = parseInt(searchParams.get("pageSize")) || 20;
    const urlTab = searchParams.get("tab") || "RTC";
    const urlSearch = searchParams.get("search") || "";

    // Parse filter params
    const urlFilters = { ...filters };
    searchParams.forEach((value, key) => {
      if (
        key !== "page" &&
        key !== "pageSize" &&
        key !== "tab" &&
        key !== "search"
      ) {
        // Special handling for customer_entity array
        if (key === 'customer_entity') {
          try {
            urlFilters[key] = JSON.parse(value);
          } catch {
            urlFilters[key] = [];
          }
        } else {
          urlFilters[key] = value;
        }
      }
    });

    // Set states
    setFilters(urlFilters);
    setCurrentPage(urlPage);
    setPageSize(urlPageSize);
    setActiveTab(urlTab);
    setGeneralSearch(urlSearch);

    // Delay initial data load to ensure state is set
    const initTimeout = setTimeout(() => {
      initializingRef.current = false;
      // Pass skipCounts=true to avoid duplicate count calls
      fetchData(
        urlPage,
        false,
        urlTab,
        urlFilters,
        urlSearch,
        urlPageSize,
        true
      );
      // Force initial count load - single call
      fetchTabCounts(urlFilters, urlSearch, true);
    }, 100);

    return () => {
      clearTimeout(initTimeout);
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      if (countTimeoutRef.current) {
        clearTimeout(countTimeoutRef.current);
      }
    };
  };

  // Update URL when state changes
  const updateURL = useCallback(
    (newFilters, newPage, newPageSize, newTab, newGeneralSearch) => {
      if (initializingRef.current) return;

      const params = new URLSearchParams();

      // Add non-empty filters
      Object.entries(newFilters).forEach(([key, value]) => {
        if (key === 'customer_entity') {
          // Handle array values for customer_entity
          if (Array.isArray(value) && value.length > 0) {
            params.set(key, JSON.stringify(value));
          }
        } else if (value && typeof value === 'string' && value.trim() !== "") {
          params.set(key, value);
        }
      });

      // Add pagination and tab info
      if (newPage > 1) params.set("page", newPage.toString());
      if (newPageSize !== 20) params.set("pageSize", newPageSize.toString());
      if (newTab !== "RTC") params.set("tab", newTab);
      if (newGeneralSearch && newGeneralSearch.trim() !== "")
        params.set("search", newGeneralSearch.trim());

      setSearchParams(params, { replace: true });
    },
    [setSearchParams]
  );

  // Updated fetchData to optionally skip count updates
  const fetchData = useCallback(
    async (
      page = 1,
      resetPage = false,
      specificTab = null,
      specificFilters = null,
      specificSearch = null,
      specificPageSize = null,
      skipCounts = false // New parameter
    ) => {
      // Prevent multiple simultaneous calls
      if (loading) {
        return;
      }

      // Wait for configurations to be loaded
      if (configLoading) {
        setTimeout(() => {
          fetchData(
            page,
            resetPage,
            specificTab,
            specificFilters,
            specificSearch,
            specificPageSize,
            skipCounts
          );
        }, 100);
        return;
      }

      setLoading(true);
      try {
        const actualPage = resetPage ? 1 : page;
        const currentTabKey = specificTab || activeTab;
        const currentFilters = specificFilters || filters;
        const currentSearch =
          specificSearch !== null ? specificSearch : generalSearch;
        const currentPageSize = specificPageSize || pageSize;

        // Build search filters
        const searchFilters = { ...currentFilters };

        // Handle date filters
        if (searchFilters.date_from && searchFilters.date_to) {
          console.log("Date range filter applied:", {
            date_from: searchFilters.date_from,
            date_to: searchFilters.date_to,
          });
        } else if (searchFilters.date_from || searchFilters.date_to) {
          delete searchFilters.date_from;
          delete searchFilters.date_to;
        }

        // Set report type based on active tab
        const currentTab = tabs.find((t) => t.key === currentTabKey);
        if (currentTab) {
          searchFilters.report_type = currentTab.reportType;
        }

        // Determine which API to use
        const trimmedSearch = currentSearch ? currentSearch.trim() : "";
        const isWildcardSearch = trimmedSearch.startsWith("*");
        const hasGeneralSearch = trimmedSearch !== "" && !isWildcardSearch;

        let result;

        if (isWildcardSearch) {
          // * prefix → search only part_number, ignore general search bar
          const partNumberTerm = trimmedSearch.slice(1).trim();
          const wildcardFilters = { ...searchFilters };
          if (partNumberTerm) {
            wildcardFilters.part_number = partNumberTerm;
          }
          result = await apiService.searchDocuments(
            wildcardFilters,
            actualPage,
            currentPageSize
          );
        } else if (hasGeneralSearch) {
          result = await apiService.combinedSearch(
            searchFilters,
            trimmedSearch,
            actualPage,
            currentPageSize
          );
        } else {
          result = await apiService.searchDocuments(
            searchFilters,
            actualPage,
            currentPageSize
          );
        }

        // Bulk-check favorites for the returned report IDs (non-blocking)
        const reportIds = (result.data || []).map((item) => item.report_id).filter(Boolean);
        if (reportIds.length > 0) {
          apiService.bulkCheckFavorites(reportIds).then((favoritedIds) => {
            setBookmarkedIds(favoritedIds);
          });
        } else {
          setBookmarkedIds([]);
        }

        setData(result.data);
        setTotalCount(result.total_count);
        setTotalPages(Math.ceil(result.total_count / currentPageSize));
        setCurrentPage(actualPage);

        // Update URL
        updateURL(
          currentFilters,
          actualPage,
          currentPageSize,
          currentTabKey,
          currentSearch
        );

        // Update tab counts only if not skipped and not initializing
        if (!skipCounts && !initializingRef.current) {
          fetchTabCounts(currentFilters, currentSearch, false);
          showToast(`Found ${result.total_count} results`, "success");
        }
      } catch (error) {
        console.error("Error fetching data:", error);
        if (!initializingRef.current) {
          showToast("Failed to fetch data", "error");
        }
        setData([]);
        setTotalCount(0);
        setTotalPages(0);
        setTabCounts({});
      } finally {
        setLoading(false);
      }
    },
    [
      filters,
      generalSearch,
      pageSize,
      activeTab,
      tabs,
      updateURL,
      showToast,
      fetchTabCounts,
      loading,
      configLoading,
    ]
  );
  // Update handleFilterChange to be more intelligent
  const handleFilterChange = (field, value) => {

    const updates = typeof field === 'object' ? field : { [field]: value };
    const newFilters = { ...filters, ...updates };
    setFilters(newFilters);

    // Clear existing timeout first
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // For date filters, check if we should trigger a search
    if (field === "date_from" || field === "date_to") {
      const bothFilled = newFilters.date_from && newFilters.date_to;
      const bothEmpty = !newFilters.date_from && !newFilters.date_to;

      if (bothFilled || bothEmpty) {
        console.log("Date filter complete - triggering search");
        searchTimeoutRef.current = setTimeout(() => {
          fetchData(1, true, null, newFilters, generalSearch);
        }, 600);
      } else {
        console.log("Waiting for both dates...");
        // Don't trigger fetch, but the filter state is updated
      }
    } else {
      // For non-date filters, proceed normally
      searchTimeoutRef.current = setTimeout(() => {
        fetchData(1, true, null, newFilters, generalSearch);
      }, 600);
    }
  };

  const handleGeneralSearch = () => {
    const trimmedSearch = generalSearch.trim();
    fetchData(1, true, null, filters, trimmedSearch);
  };

  // Update handleGeneralSearchChange to be smarter
  const handleGeneralSearchChange = (value) => {
    setGeneralSearch(value);

    // If cleared, immediately search and update counts
    if (value.trim() === "") {

      // Update URL to remove search parameter
      const params = new URLSearchParams(searchParams);
      params.delete("search");
      setSearchParams(params, { replace: true });

      // Fetch data and force count refresh since search changed
      fetchData(1, true, null, filters, "");
    }
  };

  // Updated handleClear function
  const handleClear = () => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    const clearedFilters = {
      report_type: "",
      sales_area: "",
      entity: "",
      customer_name: "",
      customer_entity: "",
      country_name: "",
      site_name: "",
      date_from: "",
      date_to: "",
      failure_reason: "",
      part_number: "",
      description: "",
      rig_model: "",
      rig_id: "",
    };

    setFilters(clearedFilters);
    setGeneralSearch("");
    setCurrentPage(1);

    // Clear URL params completely
    const params = new URLSearchParams();
    if (activeTab !== "RTC") params.set("tab", activeTab);
    setSearchParams(params, { replace: true });

    // Immediate search after clear with completely empty filters
    fetchData(1, true, null, clearedFilters, "");
  };

  // Tab handling
  const handleTabChange = (tabKey) => {
    setActiveTab(tabKey);
    setCurrentPage(1);
    fetchData(1, true, tabKey, filters, generalSearch);
  };

  // Pagination handlers
  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages) {
      fetchData(newPage);
    }
  };

  const handlePageSizeChange = (newPageSize) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
    fetchData(1, true, null, null, null, newPageSize);
  };

  // Document actions
  const handleViewDocument = (reportId) => {
    navigate(`/document/${reportId}`);
  };

  const handleDownloadDocument = async (reportId) => {
    try {
      showToast("Preparing download...", "info");
      const blob = await apiService.downloadDocument(reportId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `document_${reportId}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast("Download started", "success");
    } catch (error) {
      console.error("Download error:", error);
      showToast("Download failed", "error");
    }
  };

  const handleShareDocument = (reportId) => {
    const shareUrl = `${window.location.origin}/document/${reportId}`;

    if (navigator.share) {
      navigator.share({
        title: "Sandvik Document",
        url: shareUrl,
      });
    } else {
      navigator.clipboard.writeText(shareUrl);
      showToast("Link copied to clipboard", "success");
    }
  };

  // Bookmark handler
  const handleBookmarkDocument = async (reportId) => {
    try {
      if (bookmarkedIds.includes(reportId)) {
        await apiService.removeFavorite(reportId);
        setBookmarkedIds((prev) => prev.filter((id) => id !== reportId));
        showToast("Removed from bookmarks", "success");
      } else {
        await apiService.addFavorite(reportId);
        setBookmarkedIds((prev) => [...prev, reportId]);
        showToast("Bookmarked!", "success");
      }
    } catch (error) {
      console.error("Bookmark error:", error);
      showToast("Failed to update bookmark", "error");
    }
  };

  // View PDF handler
  const handleViewPdf = async (reportId) => {
    setPdfReportId(reportId);
    setPdfLoading(true);
    setPdfError(null);
    // Revoke previous blob URL if any
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfUrl(null);
    try {
      const blobUrl = await apiService.getPdfPreviewUrl(reportId);
      setPdfUrl(blobUrl);
    } catch (err) {
      setPdfError(err.message);
    } finally {
      setPdfLoading(false);
    }
  };

  const closePdfViewer = () => {
    if (pdfUrl) URL.revokeObjectURL(pdfUrl);
    setPdfReportId(null);
    setPdfUrl(null);
    setPdfError(null);
  };

  // Update handleExportResults to handle date filters
  const handleExportResults = async (format = "csv") => {
    try {
      showToast(`Preparing ${format.toUpperCase()} export...`, "info");

      const exportFilters = { ...filters };

      // Only include date filters if both are present
      if (!exportFilters.date_from || !exportFilters.date_to) {
        delete exportFilters.date_from;
        delete exportFilters.date_to;
      }

      const currentTab = tabs.find((t) => t.key === activeTab);
      if (currentTab) {
        exportFilters.report_type = currentTab.reportType;
      }

      const blob = await apiService.exportSearchResults(
        exportFilters,
        format,
        generalSearch
      );

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `sandvik_reports_${activeTab}_export.${format}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      showToast("Export started", "success");
    } catch (error) {
      console.error("Export error:", error);
      showToast("Export failed", "error");
    }
  };

  // Update getActiveFilters to only show date filters when both are present
  const getActiveFilters = () => {
    return Object.entries(filters).filter(([key, value]) => {
      // Special handling for date filters - only show if BOTH dates are present
      if (key === "date_from" || key === "date_to") {
        return filters.date_from && filters.date_to && value && value !== "";
      }

      // For array-based filters (e.g., customer_entity), only show if non-empty
      if (Array.isArray(value)) {
        return value.length > 0 && key !== "report_type";
      }

      // For strings/numbers, ensure non-empty string after trim and not placeholder
      if (typeof value === "string") {
        const trimmed = value.trim();
        return trimmed && trimmed !== "(Empty/Not Specified)" && key !== "report_type";
      }

      // For other types (number/boolean), basic truthiness check
      return Boolean(value) && key !== "report_type";
    });
  };

  const removeFilter = (filterKey) => {
    if (filterKey === "date_from" || filterKey === "date_to") {
      // When removing one date filter, remove both
      const newFilters = { ...filters };
      newFilters.date_from = "";
      newFilters.date_to = "";
      setFilters(newFilters);

      // Trigger fetch with both dates cleared
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      searchTimeoutRef.current = setTimeout(() => {
        fetchData(1, true, null, newFilters, generalSearch);
      }, 300);
    } else if (filterKey === "customer_name" || filterKey === "customer_entity") {
      // When removing customer name or entity, clear both
      handleFilterChange({ customer_name: "", customer_entity: [] });
    } else {
      // Preserve type when clearing: arrays should reset to []
      const clearedValue = Array.isArray(filters[filterKey]) ? [] : "";
      handleFilterChange(filterKey, clearedValue);
    }
  };

  // Add cleanup function
  useEffect(() => {
    return () => {
      if (countTimeoutRef.current) {
        clearTimeout(countTimeoutRef.current);
      }
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
      // Reset the progress flag on unmount
      countCallInProgressRef.current = false;
    };
  }, []);

  // Close search info popup when clicking outside
  useEffect(() => {
    if (!showSearchInfo) return;
    const handleClickOutside = (e) => {
      if (searchInfoRef.current && !searchInfoRef.current.contains(e.target)) {
        setShowSearchInfo(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showSearchInfo]);

  const activeFilters = getActiveFilters();

  // Show loading screen while configurations are loading
  if (configLoading) {
    return <LoadingScreen />;
  }

  return (
    <div className="h-[calc(100vh-4rem)] bg-gray-50 flex overflow-hidden">
      {loading && <LoadingScreen />}

      {/* Sidebar */}
      <div
        className={`transition-all duration-300 ease-in-out ${
          sidebarOpen ? "w-80" : "w-0"
        } overflow-hidden flex-shrink-0`}
      >
        <Sidebar
          filters={filters}
          onFilterChange={handleFilterChange}
          onClear={handleClear}
          loading={loading}
          isOpen={sidebarOpen}
          onClose={() => setSidebarOpen(false)}
        />
      </div>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-w-0 overflow-hidden">
        {/* Top Navigation Bar - Fixed */}
        <div className="bg-white border-b border-gray-200 px-6 py-2 flex-shrink-0">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              {/* Sidebar Toggle */}
              <button
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="p-2 text-gray-500 hover:text-gray-700 hover:bg-gray-100 transition-colors"
              >
                <Menu className="h-5 w-5" />
              </button>

              {/* Breadcrumb */}
              <div className="flex items-center text-sm text-gray-600">
                <button
                  onClick={() => navigate("/")}
                  className="hover:text-black transition-colors"
                >
                  Home
                </button>
                <ChevronRight className="h-4 w-4 mx-2" />
                <span className="text-gray-900">My reports</span>
              </div>
            </div>

            {/* Page Title */}
            <h1 className="text-xl text-gray-900">My reports</h1>
          </div>
        </div>

        {/* Filters and Search Section - Fixed */}
        <div className="bg-white p-6 border-b border-gray-200 flex-shrink-0">
          {/* Active Filters Display */}
          {(activeFilters.length > 0 || generalSearch.trim()) && (
            <div className="mb-6 p-4 bg-gray-50 border">
              <div className="flex items-center justify-between mb-3">
                <h3 className="text-sm text-gray-900">
                  Applied filters (
                  {activeFilters.length + (generalSearch.trim() ? 1 : 0)})
                </h3>
                <button
                  onClick={handleClear}
                  className="text-sm text-gray-600 hover:text-gray-800 flex items-center gap-1"
                >
                  <Trash2 className="h-3 w-3" />
                  Clear all
                </button>
              </div>
              <div className="flex flex-wrap gap-2">
                {generalSearch.trim() && (
                  <span className="inline-flex items-center px-3 py-1 bg-gold-g1 text-gold-g3 border">
                    <span className="mr-1 text-gold-g3">
                      {generalSearch.trim().startsWith("*") ? "Part number:" : "General Search:"}
                    </span>
                    {generalSearch}
                    <button
                      onClick={() => handleGeneralSearchChange("")}
                      className="ml-2 text-gold-g3 hover:text-gold-g3"
                    >
                      <X className="h-3 w-3" />
                    </button>
                  </span>
                )}
                {activeFilters.map(([key, value]) => {
                  // Convert array values to comma-separated string for display
                  const displayValue = Array.isArray(value) ? value.join(', ') : value;
                  return (
                    <span
                      key={key}
                      className="inline-flex items-center px-3 py-1 bg-gray-200 text-gray-800 border"
                    >
                      <span className="mr-1 text-gray-600">
                        {(() => {
                          const text = key.replace(/_/g, " ");
                          return text.charAt(0).toUpperCase() + text.slice(1).toLowerCase();
                        })()}
                        :
                      </span>
                      {displayValue.length > 25 ? `${displayValue.substring(0, 25)}...` : displayValue}
                      <button
                        onClick={() => removeFilter(key)}
                        className="ml-2 text-gray-600 hover:text-gray-800"
                      >
                        <X className="h-3 w-3" />
                      </button>
                    </span>
                  );
                })}
              </div>
            </div>
          )}

          {/* General Search Bar */}
          <div className="mb-4">
            <div className="flex items-center space-x-4">
              <div className="flex-1 max-w-md relative">
                <input
                  type="text"
                  placeholder="Search across site names, customer names, failure reasons, serial numbers..."
                  value={generalSearch}
                  onChange={(e) => handleGeneralSearchChange(e.target.value)}
                  onKeyPress={(e) => e.key === "Enter" && handleGeneralSearch()}
                  className="w-full border border-gray-300 pl-10 pr-4 py-2 focus:outline-none focus:ring-2 focus:ring-black focus:border-electric-blue"
                />
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-n5" />
              </div>
              <button
                onClick={handleGeneralSearch}
                disabled={loading}
                className="bg-electric-blue text-white px-4 py-2 hover:bg-electric-blue-eb3 disabled:opacity-50 flex items-center gap-2"
              >
                <Search className="h-4 w-4" />
                Search
              </button>
              {/* Info icon */}
              <div className="relative" ref={searchInfoRef}>
                <button
                  onClick={() => setShowSearchInfo((v) => !v)}
                  className="p-1.5 text-n5 hover:text-gray-700 hover:bg-gray-100 rounded-full transition-colors"
                  title="How does search work?"
                >
                  <Info className="h-4 w-4" />
                </button>
                {showSearchInfo && (
                  <div className="absolute right-0 top-8 z-50 w-72 bg-white border border-gray-200 shadow-lg p-3 text-left">
                    <div className="flex items-center justify-between mb-2">
                      <h4 className="text-xs text-gray-900">How search works</h4>
                      <button
                        onClick={() => setShowSearchInfo(false)}
                        className="text-n5 hover:text-gray-600"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <div className="space-y-2 text-xs text-gray-700">
                      <div>
                        <p className=" text-gray-800 mb-1">General search</p>
                        <p className="text-gray-500">e.g. <code className="bg-gray-100 px-1 font-mono">Atlas</code> → matches customer names, site names, serial numbers</p>
                      </div>
                      <div>
                        <p className=" text-gray-800 mb-1">Sidebar filter (partial)</p>
                        <p className="text-gray-500">e.g. <code className="bg-gray-100 px-1 font-mono">81</code> → finds any part number containing 81</p>
                      </div>
                      <div className="bg-gold-g1 border border-gold-g2 p-2">
                        <p className=" text-gold-g3 mb-1">Wildcard <code className="bg-gold-g1 px-0.5 font-mono">*</code></p>
                        <p className="text-gold-g3 mb-1">Prefix with <code className="bg-gold-g1 px-0.5 font-mono">*</code> to match <span className="">part numbers ending with</span> a value:</p>
                        <p className="text-gold-g3"><code className="bg-gold-g1 px-1 font-mono">*81</code> → part numbers ending in 81</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>

          {/* Tabs with Counts */}
          <div className="mb-0">
            <div className="border-b border-gray-200">
              <nav className="-mb-px flex space-x-8">
                {tabs.map((tab) => (
                  <button
                    key={tab.key}
                    onClick={() => handleTabChange(tab.key)}
                    className={`py-2 px-1 border-b-2 text-sm flex items-center gap-2 ${
                      activeTab === tab.key
                        ? "border-electric-blue text-black"
                        : "border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300"
                    }`}
                  >
                    {tab.label}
                    {tabCounts[tab.key] !== undefined && (
                      <span
                        className={`px-2 py-0.5 ${
                          activeTab === tab.key
                            ? "bg-electric-blue text-white"
                            : "bg-gray-100 text-gray-600"
                        }`}
                      >
                        {tabCounts[tab.key]}
                      </span>
                    )}
                  </button>
                ))}
              </nav>
            </div>
          </div>
        </div>

        {/* Table Container - Takes remaining space with proper scrolling */}
        <div
          className="flex-1 overflow-hidden flex flex-col min-h-0"
          style={{ paddingLeft: "1rem", paddingRight: "1rem" }}
        >
          <div className="flex-1 overflow-auto">
            <TableComponent
              data={data}
              loading={loading}
              filters={filters}
              activeTab={activeTab}
              onViewDocument={handleViewDocument}
              onDownloadDocument={handleDownloadDocument}
              onShareDocument={handleShareDocument}
              onBookmarkDocument={handleBookmarkDocument}
              onViewPdf={handleViewPdf}
              bookmarkedIds={bookmarkedIds}
            />
          </div>

          {/* Pagination - Inside table container to avoid overlap */}
          <div className="bg-white flex-shrink-0">
            <Pagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalCount={totalCount}
              totalPages={totalPages}
              onPageChange={handlePageChange}
              onPageSizeChange={handlePageSizeChange}
            />
          </div>
        </div>
      </div>

      {/* PDF Viewer Modal */}
      {pdfReportId && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm" onClick={closePdfViewer}>
          <div className="bg-white w-[90vw] h-[90vh] shadow-2xl flex flex-col" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between px-6 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex items-center space-x-3">
                <FileText className="h-5 w-5 text-gray-700" />
                <span className=" text-gray-800">Report PDF — {pdfReportId}</span>
              </div>
              <div className="flex items-center space-x-2">
                {pdfUrl && (
                  <a href={pdfUrl} target="_blank" rel="noopener noreferrer"
                    className="px-3 py-1.5 text-xs text-gray-600 hover:text-black border border-gray-300 hover:border-gray-400 transition-colors flex items-center space-x-1">
                    <ExternalLink className="h-3.5 w-3.5" /><span>Open in tab</span>
                  </a>
                )}
                <button onClick={closePdfViewer} className="p-1.5 hover:bg-gray-200 transition-colors">
                  <X className="h-5 w-5 text-gray-600" />
                </button>
              </div>
            </div>
            <div className="flex-1 relative">
              {pdfLoading && (
                <div className="absolute inset-0 flex items-center justify-center bg-gray-50">
                  <RefreshCw className="h-8 w-8 text-n5 animate-spin" />
                </div>
              )}
              {pdfError && (
                <div className="absolute inset-0 flex flex-col items-center justify-center bg-gray-50">
                  <AlertCircle className="h-10 w-10 text-red-400 mb-3" />
                  <p className="text-sm text-gray-600">{pdfError}</p>
                </div>
              )}
              {pdfUrl && !pdfLoading && (
                <object data={pdfUrl} type="application/pdf" className="w-full h-full">
                  <embed src={pdfUrl} type="application/pdf" className="w-full h-full" />
                </object>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
